/**
 * Widget Header Component
 *
 * Provider-branded header for widget pages with navigation
 * Shows logo, company name, and authentication-based navigation
 */

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useProviderConfig } from '../../contexts/ProviderConfigContext';
import { useWidgetAuth } from '../../contexts/WidgetAuthContext';

export function WidgetHeader() {
  const { config } = useProviderConfig();
  const { user, isAuthenticated, logout } = useWidgetAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('../login');
  };

  if (!config) return null;

  const isActive = (path: string) => location.pathname.includes(path);

  return (
    <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Company Name */}
          <Link to=".." className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            {config.logoUrl && (
              <img
                src={config.logoUrl}
                alt={`${config.companyName} logo`}
                className="h-10 w-auto"
              />
            )}
            <span className="text-xl font-bold text-gray-900">{config.companyName}</span>
          </Link>

          {/* Navigation */}
          <nav className="flex items-center space-x-1">
            {isAuthenticated && user ? (
              <>
                {/* Dashboard Link */}
                <Link
                  to="../dashboard"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('dashboard')
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  Dashboard
                </Link>

                {/* Account Link */}
                <Link
                  to="../account"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('account')
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  Account
                </Link>

                {/* User Menu - Simple version */}
                <div className="flex items-center space-x-3 ml-4 pl-4 border-l border-gray-200">
                  <span className="text-sm text-gray-600 hidden sm:inline">
                    {user.name}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Public Navigation */}
                <Link
                  to="../login"
                  className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="../register"
                  className="ml-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-primary hover:bg-primary/90 transition-colors shadow-sm"
                >
                  Get Started
                </Link>
              </>
            )}
          </nav>
        </div>
      </div>

      {/* Contact Bar - Optional secondary bar */}
      <div className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-center space-x-6 text-xs text-gray-600">
            <a
              href={`tel:${config.contactPhone}`}
              className="flex items-center hover:text-primary transition-colors"
            >
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              {config.contactPhone}
            </a>
            <a
              href={`mailto:${config.contactEmail}`}
              className="flex items-center hover:text-primary transition-colors"
            >
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              {config.contactEmail}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
